'use client';
import { MissingImage } from '@dealerportal/catalog-ui';
import { useCiCItemLookup } from '@dealerportal/catalog-ui/Hooks/useCiCItemLookup';
import type { ProductInformation } from '@dealerportal/common';
import { useLocale, useTranslations } from '@dealerportal/i18n/client';
import {
  useClientContext,
  useFavouriteProducts,
  useProductSearch,
} from '@dealerportal/react-helpers';
import { Gallery } from '@dealerportal/ui-components/Gallery';
import { Pills } from '@dealerportal/ui-components/Pills';
import { FormatDate, isJSON, remapBrandName } from '@dealerportal/utilities';
import truncate from 'lodash/truncate';
import type { ComponentProps } from 'react';
import { MarkFavourite } from '../../../catalog-ui/src/lib/Modules/ProductCard/MarkFavourite';
import { ProductLink } from '../../../catalog-ui/src/lib/Modules/ProductCard/ProductLink';

type SharedProps = {
  count?: number | string;
  galleryProps?: ComponentProps<typeof Gallery>;
} & ComponentProps<typeof Gallery>;

// Shared rendering component — receives already-fetched, typed products
const RecentProductsView = ({
  products,
  isLoading,
  count = '20',
  arrows,
  arrowposition,
  autoPlay,
  partialvisible,
  partialvisibilitygutter,
  slidestoslide,
  removeArrowOnDeviceType,
  items,
  itemclass,
  infinite,
  responsive,
}: { products: ProductInformation[]; isLoading: boolean } & SharedProps) => {
  const ctx = useClientContext();
  const favouriteContext = useFavouriteProducts();
  const t = useTranslations('products');
  const locale = useLocale() as 'en' | 'pt' | 'es';

  const displayProducts = products
    .map((product) => ({
      ...product,
      lastViewedDate: ctx.recentProducts.find((r) => r.UPID === product.UPID)
        ?.lastViewedDate,
    }))
    .filter((p) => p.lastViewedDate)
    .sort(
      (a, b) =>
        new Date(b.lastViewedDate!).getTime() -
        new Date(a.lastViewedDate!).getTime()
    );

  const productCount =
    typeof count === 'string' ? Number.parseInt(count, 10) : count;

  const galleryConfig = {
    count: productCount,
    arrows: arrows || 'true',
    arrowposition: arrowposition || 'inside',
    removeArrowOnDeviceType: removeArrowOnDeviceType || 'md',
    autoPlay: autoPlay || false,
    infinite: infinite || 'false',
    itemclass: itemclass || 'ui-px-2 first:!ui-pl-0 last:!ui-pr-0',
    partialvisible: partialvisible || 'true',
    responsive: isJSON(responsive)
      ? responsive
      : JSON.stringify({
          md: {
            breakpoint: { max: 767, min: 0 },
            items: 1,
            partialVisibilityGutter: Number(partialvisibilitygutter) || 40,
            slidesToSlide: 1,
          },
          lg: {
            breakpoint: { max: 1023, min: 768 },
            items: 2,
            partialVisibilityGutter: Number(partialvisibilitygutter) || 40,
            slidesToSlide: 1,
          },
          xl: {
            breakpoint: { max: 1279, min: 1024 },
            items: Number(items) || 3,
            partialVisibilityGutter: Number(partialvisibilitygutter) || 20,
            slidesToSlide: Number(slidestoslide) || 1,
          },
          '2xl': {
            breakpoint: { max: 99999, min: 1280 },
            items: Number(items) || 3,
            partialVisibilityGutter: Number(partialvisibilitygutter) || 40,
            slidesToSlide: Number(slidestoslide) || 2,
          },
        }),
  };

  if (isLoading || displayProducts.length === 0) {
    return null;
  }

  const handleFavoriteClick = (upid: string) => {
    const product = displayProducts.find((p) => p.UPID === upid);
    if (!product) return;

    const favouriteProduct = {
      ...product,
      isSelected: true,
    };

    if (favouriteContext?.addRemoveProductsFromFav) {
      favouriteContext.addRemoveProductsFromFav(favouriteProduct);
    }
  };

  const getFormattedLastViewedDate = (upid: string) => {
    const recentProduct = ctx?.recentProducts.find(
      (recent) => recent.UPID === upid
    );
    const lastViewedDate = recentProduct?.lastViewedDate
      ? new Date(recentProduct.lastViewedDate)
      : null;

    if (!lastViewedDate) return t('dateNotAvailable');

    const now = new Date();
    const timeDiff = now.getTime() - lastViewedDate.getTime();
    const oneDay = 24 * 60 * 60 * 1000;

    // Use 24-hour format for pt/es, 12-hour format for en
    const timeFormat = locale === 'en' ? 'h:mm a' : 'HH:mm';

    if (timeDiff < oneDay) {
      return `${t('todayAt')} ${FormatDate(lastViewedDate, timeFormat, locale)}`;
    }
    if (timeDiff < 2 * oneDay) {
      return `${t('yesterdayAt')} ${FormatDate(lastViewedDate, timeFormat, locale)}`;
    }
    if (timeDiff < 7 * oneDay) {
      return t('lastWeek');
    }
    if (timeDiff < 14 * oneDay) {
      return t('overAWeekAgo');
    }
    return FormatDate(lastViewedDate, 'yyyy-MM-dd', locale);
  };

  return (
    <div className="srp__recentProducts ui-mb-10">
      <div className="srp__recentProducts ui-mb-3">
        <h3 id="headline" className="!ui-text-left !ui-text-xl">
          {t('recentProducts')}
        </h3>
      </div>
      <div className="srp_recentProductsCarouselWrapper">
        <Gallery {...galleryConfig}>
          {displayProducts.map((product, index) => {
            const imageURL = product.swatches?.[0]?.imageURL;
            const resolvedImageURL = imageURL?.includes('res.cloudinary.com')
              ? imageURL.replace('https://images.floorforce.com/Textures/', '')
              : imageURL;

            return (
              <ProductLink
                product={product}
                key={index}
                style={{
                  fontFamily: 'inherit',
                }}
                className="srp__recentProductCard ui-relative ui-border ui-border-gray-300 ui-rounded-md ui-overflow-hidden ui-p-3 ui-flex ui-bg-white"
              >
                <div className="ui-h-full" id={`product-link-${product.UPID}`}>
                  {!imageURL && (
                    <MissingImage
                      className="ui-object-cover ui-rounded-md"
                      width={72}
                      height={72}
                    />
                  )}
                  {imageURL && (
                    <img
                      src={resolvedImageURL}
                      alt={`${product.productName} by ${product.brandName}`}
                      title={`${product.productName} by ${product.brandName}`}
                      className="ui-size-[72px] ui-object-cover ui-rounded-md"
                    />
                  )}
                </div>

                <div className="ui-absolute ui-top-1 ui-right-1">
                  <MarkFavourite
                    btnClass="'!ui-min-w-7 !ui-min-h-7 !ui-size-7 ui-shrink-0' !ui-shadow-none"
                    iconClass="!ui-size-5"
                    isFavourite={favouriteContext?.find({
                      UPID: product.UPID,
                    })}
                    onClick={() => handleFavoriteClick(product.UPID)}
                  />
                </div>

                <div className="ui-flex ui-flex-col ui-justify-between ui-ml-3 ui-flex-1">
                  <div>
                    <div className="!ui-text-[11px] !ui-leading-none ui-mb-1 !ui-font-normal ui-text-gray-500">
                      {product.UPID && getFormattedLastViewedDate(product.UPID)}
                    </div>
                    <div className="recentproducts__brandName !ui-font-semibold ui-text-gray-800 !ui-visibleui-text-base">
                      {remapBrandName(product.brandName)}
                    </div>
                    <div className="recentproducts__productName !ui-font-normal !ui-text-sm ui-text-gray-600 ui-whitespace-nowrap">
                      {truncate(
                        `${product.productName} - ${product.colorName}`,
                        {
                          length: 30,
                        }
                      )}
                    </div>
                  </div>
                  <div className="ui-flex ui-items-center ui-gap-2 ui-mt-2">
                    {product.hasSample && (
                      <Pills
                        pills={[
                          {
                            text: t('samplesAvailable'),
                            backgroundColor: 'primary',
                            fontColor: 'white',
                            visible: true,
                            radius: 'ui-rounded-full',
                            className: '!ui-text-[10px] !ui-mb-0',
                          },
                        ]}
                        dispatch={null}
                      />
                    )}
                    {product.hasVisualization && (
                      <Pills
                        pills={[
                          {
                            text: t('seeInMyRoom'),
                            backgroundColor: 'neutral',
                            fontColor: 'black',
                            visible: true,
                            radius: 'ui-rounded-full',
                            className:
                              '!ui-text-[10px] ui-border ui-border-solid ui-border-gray-700 !ui-mb-0',
                          },
                        ]}
                        dispatch={null}
                      />
                    )}
                  </div>
                </div>
              </ProductLink>
            );
          })}
        </Gallery>
      </div>
    </div>
  );
};

// Fetches products via CiC item lookup
const RecentProductsCiC = (props: SharedProps) => {
  const ctx = useClientContext();
  const itemCodes = ctx.recentProducts
    .filter((x) => x.slug && x.sku)
    .map((x) => `${x.slug}-${x.sku}`.replace(/\-missing-sku/g, ''));
  const { products, isLoading } = useCiCItemLookup(itemCodes);
  return (
    <RecentProductsView products={products} isLoading={isLoading} {...props} />
  );
};

// Fetches products via the traditional search API using UPIDs
const RecentProductsSearch = (props: SharedProps) => {
  const ctx = useClientContext();
  const productCount =
    typeof props.count === 'string'
      ? Number.parseInt(props.count, 10)
      : (props.count ?? 20);
  const upids = ctx.recentProducts
    .map((x) => x.UPID)
    .filter((upid): upid is string => Boolean(upid))
    .join(',');
  const { products, loading } = useProductSearch(
    `upids=${upids}`,
    productCount
  );
  return (
    <RecentProductsView
      products={products as unknown as ProductInformation[]}
      isLoading={loading}
      {...props}
    />
  );
};

// Public entry point — routes to the appropriate data source
export const RecentProducts = (props: SharedProps) => {
  const ctx = useClientContext();
  if (!ctx.recentProducts.length) return null;
  return ctx.useCiC ? (
    <RecentProductsCiC {...props} />
  ) : (
    <RecentProductsSearch {...props} />
  );
};

export default RecentProducts;
